import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { checklist as checklistApi, files as filesApi, instructionTemplates as tplApi } from "../lib/api";
import { setFor } from "../lib/responseSets";
import { localizedQuestion } from "../lib/i18nContent";
import { toast } from "sonner";
import {
  Workflow, Plus, Trash2, Check, X, MessageSquare, Camera, MessageSquareText,
  CornerDownRight, AlertTriangle, Bell, Lock, Loader2, Flag, BookOpen, Paperclip,
  Eye, FileText, Save,
} from "lucide-react";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `r${Date.now()}${Math.floor(Math.random() * 1e4)}`;

function newRule(on = ["no"]) {
  return {
    id: uid(),
    on,
    triggers: {
      instruction: "",
      require_photo: false,
      require_comment: false,
      followup_question: "",
      followup_required: false,
      create_action: false,
      action_severity: "Media",
      action_assignee: "",
      action_due_days: 3,
      notify: false,
      block_close: false,
      manual_file: null,
    },
  };
}

// Derive the answer options + default flagged set from the question's response
// type so logic can be authored for good_fair_poor / pass_fail / compliant /
// safe_risk (not just the canales set). Data-capture types (text/number/media/
// signature) have no discrete answers, so they expose no per-answer chips.
function optionsForQuestion(question) {
  const rt = question?.response_type || "canales";
  const rset = setFor(rt);
  const values = rset.scored ? rset.options.map((o) => o.v) : [];
  const flagged = rset.flagged || [];
  return { scored: !!rset.scored, values, flagged };
}

export default function ChecklistLogicDialog({ open, onOpenChange, question, onSaved }) {
  const { t, i18n } = useTranslation();
  const [rules, setRules] = useState([]);
  const [flagged, setFlagged] = useState(null); // null => use default
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [uploadingRule, setUploadingRule] = useState(null);

  const { scored: isScored, values: responseValues, flagged: defaultFlagged } = optionsForQuestion(question);
  const defaultRuleOn = defaultFlagged[0] ? [defaultFlagged[0]] : (responseValues[0] ? [responseValues[0]] : []);

  useEffect(() => {
    if (!open || !question) return;
    const existing = (question.logic || []).map((r) => ({
      id: r.id || uid(),
      on: r.on || [],
      triggers: { ...newRule().triggers, ...(r.triggers || {}) },
    }));
    setRules(existing.length ? existing : [newRule(defaultRuleOn)]);
    setFlagged(question.flagged_responses ?? null);
    tplApi.list().then(setTemplates).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, question]);

  const respLabel = (v) => t(`audit.${v}`);

  const toggleOn = (ruleId, v) =>
    setRules((rs) => rs.map((r) => {
      if (r.id !== ruleId) return r;
      const set = new Set(r.on);
      if (set.has(v)) set.delete(v); else set.add(v);
      return { ...r, on: Array.from(set) };
    }));

  const setTrig = (ruleId, key, val) =>
    setRules((rs) => rs.map((r) => (r.id === ruleId ? { ...r, triggers: { ...r.triggers, [key]: val } } : r)));

  const insertTemplate = (ruleId, text) => {
    if (!text) return;
    setRules((rs) => rs.map((r) => {
      if (r.id !== ruleId) return r;
      const cur = r.triggers.instruction || "";
      const next = cur.trim() ? `${cur.trim()}\n${text}` : text;
      return { ...r, triggers: { ...r.triggers, instruction: next } };
    }));
  };

  const saveAsTemplate = async (ruleId) => {
    const rule = rules.find((r) => r.id === ruleId);
    const text = (rule?.triggers?.instruction || "").trim();
    if (!text) return toast.error(t("logic.tpl_empty"));
    try {
      const created = await tplApi.create("", text);
      setTemplates((ts) => [created, ...ts]);
      toast.success(t("logic.tpl_saved"));
    } catch {
      toast.error(t("logic.error"));
    }
  };

  const uploadManual = async (ruleId, file) => {
    if (!file) return;
    setUploadingRule(ruleId);
    try {
      const rec = await filesApi.upload(file, "", "manual");
      setTrig(ruleId, "manual_file", {
        file_id: rec.id,
        name: rec.original_filename || file.name,
        url: filesApi.url(rec.id),
        content_type: rec.content_type || file.type,
      });
    } catch {
      toast.error(t("logic.error"));
    } finally {
      setUploadingRule(null);
    }
  };

  const addRule = () => setRules((rs) => [...rs, newRule(defaultRuleOn)]);
  const removeRule = (ruleId) => setRules((rs) => rs.filter((r) => r.id !== ruleId));

  const usingDefault = flagged === null;
  const flaggedSet = new Set(usingDefault ? defaultFlagged : flagged);
  const toggleFlagged = (v) => {
    if (usingDefault) return;
    setFlagged((f) => {
      const set = new Set(f || []);
      if (set.has(v)) set.delete(v); else set.add(v);
      return Array.from(set);
    });
  };

  const save = async () => {
    const clean = rules
      .map((r) => ({ id: r.id, on: r.on, triggers: r.triggers }))
      .filter((r) => (r.on || []).length > 0);
    setSaving(true);
    try {
      await checklistApi.setLogic(question.id, clean, usingDefault ? null : flagged);
      toast.success(t("logic.saved"));
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("logic.error"));
    } finally {
      setSaving(false);
    }
  };

  const TrigToggle = ({ ruleId, tkey, icon: Icon, label }) => {
    const rule = rules.find((r) => r.id === ruleId);
    const val = !!rule?.triggers?.[tkey];
    return (
      <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${val ? "border-[#1E3A8A] bg-[#E5EBFA]/40" : "border-stone-200"}`}>
        <Icon size={14} className={val ? "text-[#1E3A8A]" : "text-stone-400"} />
        <span className="text-xs font-medium flex-1">{label}</span>
        <Switch checked={val} onCheckedChange={(v) => setTrig(ruleId, tkey, v)} data-testid={`trig-${tkey}-${ruleId}`} />
      </label>
    );
  };

  if (!question) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="logic-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Workflow size={20} className="text-[#1E3A8A]" /> {t("logic.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500 line-clamp-2">
            {localizedQuestion(question, i18n.language).question}
          </DialogDescription>
        </DialogHeader>

        {/* Flagged responses */}
        <div className="rounded-xl border border-stone-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Flag size={14} className="text-amber-600" />
              <span className="text-sm font-semibold">{t("logic.flagged_title")}</span>
            </div>
            <label className="flex items-center gap-2 text-xs text-stone-500">
              {t("logic.use_default")}
              <Switch
                checked={usingDefault}
                onCheckedChange={(v) => setFlagged(v ? null : [...defaultFlagged])}
                data-testid="flagged-use-default"
              />
            </label>
          </div>
          <p className="text-[11px] text-stone-500 mt-1">{t("logic.flagged_hint")}</p>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {responseValues.map((v) => {
              const active = flaggedSet.has(v);
              return (
                <button
                  key={v}
                  type="button"
                  disabled={usingDefault}
                  onClick={() => toggleFlagged(v)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all disabled:opacity-60 ${active ? "bg-amber-500 text-white border-amber-500" : "bg-white text-stone-600 border-stone-200"}`}
                  data-testid={`flagged-${v}`}
                >
                  {active && <Check size={11} className="inline mr-1 -mt-0.5" />}
                  {respLabel(v)}
                </button>
              );
            })}
          </div>
          {!isScored && (
            <p className="text-[11px] text-stone-400 mt-2" data-testid="logic-no-answer-options">{t("logic.no_answer_options")}</p>
          )}
        </div>

        {/* Rules */}
        <div className="space-y-4 mt-1">
          {rules.map((rule, idx) => (
            <div key={rule.id} className="rounded-xl border border-stone-200 p-4" data-testid={`logic-rule-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="canales-overline">{t("logic.if_answer")}</span>
                {rules.length > 1 && (
                  <button type="button" onClick={() => removeRule(rule.id)} className="text-stone-400 hover:text-red-600" data-testid={`remove-rule-${idx}`}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {responseValues.map((v) => {
                  const active = rule.on.includes(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => toggleOn(rule.id, v)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-stone-600 border-stone-200"}`}
                      data-testid={`rule-on-${idx}-${v}`}
                    >
                      {active && <Check size={11} className="inline mr-1 -mt-0.5" />}
                      {respLabel(v)}
                    </button>
                  );
                })}
              </div>

              <div className="canales-overline mt-4">{t("logic.then")}</div>

              {/* Instruction */}
              <div className="mt-2">
                <label className="text-xs font-medium flex items-center gap-1.5 text-stone-600">
                  <MessageSquare size={13} /> {t("logic.trg_instruction")}
                </label>
                <Textarea
                  value={rule.triggers.instruction}
                  onChange={(e) => setTrig(rule.id, "instruction", e.target.value)}
                  rows={2}
                  placeholder={t("logic.trg_instruction_ph")}
                  className="mt-1 text-sm"
                  data-testid={`trig-instruction-${rule.id}`}
                />
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <Select value="" onValueChange={(v) => insertTemplate(rule.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-56" data-testid={`tpl-select-${rule.id}`}>
                      <SelectValue placeholder={t("logic.tpl_insert")} />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {templates.length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-stone-400">{t("logic.tpl_none")}</div>
                      )}
                      {templates.map((tpl) => (
                        <SelectItem key={tpl.id} value={tpl.text}>{tpl.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => saveAsTemplate(rule.id)}
                    className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] font-medium hover:underline"
                    data-testid={`tpl-save-${rule.id}`}
                  >
                    <Save size={12} /> {t("logic.tpl_save")}
                  </button>
                </div>
              </div>

              {/* Manual / reference file */}
              <div className="mt-3">
                <label className="text-xs font-medium flex items-center gap-1.5 text-stone-600">
                  <BookOpen size={13} /> {t("logic.trg_manual")}
                </label>
                <p className="text-[11px] text-stone-400">{t("logic.trg_manual_hint")}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 text-xs font-semibold cursor-pointer hover:border-stone-300">
                    {uploadingRule === rule.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                    {t("logic.trg_manual_upload")}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,application/pdf"
                      onChange={(e) => uploadManual(rule.id, (e.target.files || [])[0])}
                      data-testid={`manual-upload-${rule.id}`}
                    />
                  </label>
                  {rule.triggers.manual_file?.url && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-stone-100 text-xs" data-testid={`manual-chip-${rule.id}`}>
                      <FileText size={12} /> {(rule.triggers.manual_file.name || "").slice(0, 22)}
                      <a href={rule.triggers.manual_file.url} target="_blank" rel="noreferrer" className="text-[#1E3A8A]"><Eye size={12} /></a>
                      <button type="button" onClick={() => setTrig(rule.id, "manual_file", null)} className="text-stone-400 hover:text-red-600"><X size={12} /></button>
                    </span>
                  )}
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                <TrigToggle ruleId={rule.id} tkey="require_photo" icon={Camera} label={t("logic.trg_require_photo")} />
                <TrigToggle ruleId={rule.id} tkey="require_comment" icon={MessageSquareText} label={t("logic.trg_require_comment")} />
                <TrigToggle ruleId={rule.id} tkey="create_action" icon={AlertTriangle} label={t("logic.trg_create_action")} />
                <TrigToggle ruleId={rule.id} tkey="notify" icon={Bell} label={t("logic.trg_notify")} />
                <TrigToggle ruleId={rule.id} tkey="block_close" icon={Lock} label={t("logic.trg_block_close")} />
              </div>

              {/* Follow-up question */}
              <div className="mt-3">
                <label className="text-xs font-medium flex items-center gap-1.5 text-stone-600">
                  <CornerDownRight size={13} /> {t("logic.trg_followup")}
                </label>
                <Input
                  value={rule.triggers.followup_question}
                  onChange={(e) => setTrig(rule.id, "followup_question", e.target.value)}
                  placeholder={t("logic.trg_followup_ph")}
                  className="mt-1 text-sm"
                  data-testid={`trig-followup-${rule.id}`}
                />
                {rule.triggers.followup_question && (
                  <label className="flex items-center gap-2 text-xs text-stone-500 mt-1.5">
                    <Switch checked={rule.triggers.followup_required} onCheckedChange={(v) => setTrig(rule.id, "followup_required", v)} data-testid={`trig-followup-required-${rule.id}`} />
                    {t("logic.trg_followup_required")}
                  </label>
                )}
              </div>

              {/* Create action details */}
              {rule.triggers.create_action && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-lg bg-stone-50 p-3">
                  <div>
                    <label className="text-[11px] font-medium text-stone-500">{t("logic.trg_action_severity")}</label>
                    <Select value={rule.triggers.action_severity} onValueChange={(v) => setTrig(rule.id, "action_severity", v)}>
                      <SelectTrigger className="mt-1 h-9" data-testid={`action-severity-${rule.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Alta">{t("severity.Alta")}</SelectItem>
                        <SelectItem value="Media">{t("severity.Media")}</SelectItem>
                        <SelectItem value="Baja">{t("severity.Baja")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-stone-500">{t("logic.trg_action_assignee")}</label>
                    <Input value={rule.triggers.action_assignee} onChange={(e) => setTrig(rule.id, "action_assignee", e.target.value)} placeholder={t("logic.trg_action_assignee_ph")} className="mt-1 h-9 text-sm" data-testid={`action-assignee-${rule.id}`} />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-stone-500">{t("logic.trg_action_due")}</label>
                    <Input type="number" min="0" value={rule.triggers.action_due_days ?? ""} onChange={(e) => setTrig(rule.id, "action_due_days", e.target.value === "" ? null : parseInt(e.target.value, 10))} className="mt-1 h-9 text-sm" data-testid={`action-due-${rule.id}`} />
                  </div>
                </div>
              )}

              {/* Live preview */}
              <RulePreview rule={rule} respLabel={respLabel} t={t} />
            </div>
          ))}

          <Button variant="outline" onClick={addRule} className="gap-1.5 rounded-full" data-testid="add-logic-rule">
            <Plus size={14} /> {t("logic.add_rule")}
          </Button>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button className="brand-button gap-2" onClick={save} disabled={saving} data-testid="logic-save-button">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t("logic.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RulePreview({ rule, respLabel, t }) {
  const tr = rule.triggers || {};
  const badges = [];
  if (tr.require_photo) badges.push(t("logic.trg_require_photo"));
  if (tr.require_comment) badges.push(t("logic.trg_require_comment"));
  if (tr.followup_question) badges.push(t("logic.trg_followup"));
  if (tr.create_action) badges.push(t("logic.trg_create_action"));
  if (tr.notify) badges.push(t("logic.trg_notify"));
  if (tr.block_close) badges.push(t("logic.trg_block_close"));
  if (tr.manual_file?.url) badges.push(t("logic.trg_manual"));
  const answers = (rule.on || []).map(respLabel).join(", ") || "—";
  return (
    <div className="mt-4 rounded-lg border border-dashed border-[#1E3A8A]/40 bg-[#E5EBFA]/30 p-3" data-testid={`rule-preview-${rule.id}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#1E3A8A] mb-2">
        <Eye size={12} /> {t("logic.preview_title")}
      </div>
      <p className="text-xs text-stone-600">
        {t("logic.preview_when", { answers })}
      </p>
      {tr.instruction && (
        <div className="flex items-start gap-2 rounded-md border-l-4 border-[#1E3A8A] bg-white px-2.5 py-1.5 mt-2">
          <AlertTriangle size={13} className="text-[#1E3A8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#1E3A8A]">{tr.instruction}</p>
        </div>
      )}
      {tr.manual_file?.url && (
        <div className="flex items-center gap-1.5 text-[11px] text-stone-600 mt-2 bg-white border border-stone-200 rounded-md px-2 py-1 w-fit">
          <BookOpen size={12} /> {(tr.manual_file.name || "").slice(0, 26)}
        </div>
      )}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {badges.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-stone-200 text-[10.5px] text-stone-600">
              <Check size={10} className="text-emerald-600" /> {b}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
